import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faSearch,
  faSliders,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import {
  setCategoryName,
  setSearchState,
  sortCollecton,
} from '../../../ngrx/data.action';
import {
  selectCategories,
  selectCategoryName,
  selectCollectionSearch,
  selectSearch,
  selectSortCollection,
} from '../../../ngrx/data.reducer';
import { HelperService } from '../../../services/helper.service';
import { ResizeService } from '../../../services/resize.service';
import { ScrollService } from '../../../services/scroll.service';
import { CategoryTrayComponent } from '../../../shared/category-tray/category-tray.component';
import { FooterComponent } from '../../../shared/footer/footer.component';
import { HeaderComponent } from '../../../shared/header/header.component';
import { MiniLoadingComponent } from '../../../shared/loaders/mini-loader/mini-loading.component';
import { SearchSuggestionsComponent } from '../../../shared/search-suggestions/search-suggestions.component';
declare var $: any;

@Component({
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    FontAwesomeModule,
    HeaderComponent,
    FooterComponent,
    CategoryTrayComponent,
    LazyLoadImageModule,
    FormsModule,
    SearchSuggestionsComponent,
  ],
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  scrollData;
  currentUrl: string;
  faSliders = faSliders;
  faTimes = faTimesCircle;
  faSearch = faSearch;
  Categories: any[] = [];
  pageNumber = 1;
  searchValue: any;
  pageSize = 12;
  loadMoreBtnShown: boolean = false;
  loader_collection_btn: boolean = false;
  loading: boolean = false;
  selectedCategory: number;
  selectedSubCategory: number;
  mentorSearchQuery: string = null;
  mentorSearchQueryUpdate = new Subject<any>();
  private routeName: string;
  private subRouteName: string;
  public sortValue = 10;
  categories$: Observable<any> = this.store.select(selectCategories);
  category: any;
  subCategory: any;
  private destroy$ = new Subject<void>();
  showTray: boolean = false;
  sortCollection$: Observable<any> = this.store.select(selectSortCollection);
  categoryName: string;
  categoryName$ = this.store.select(selectCategoryName);
  searching;
  searching$ = this.store.select(selectCollectionSearch);
  searchState;
  search$ = this.store.select(selectSearch);
  scrollPosition: string;
  show: boolean = false;
  showHeader: boolean = true;

  @ViewChild('suggestions', { static: false }) suggestions: ElementRef;
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.suggestions && this.suggestions.nativeElement) {
      const clickedInside = this.suggestions.nativeElement.contains(
        event.target,
      );
      if (!clickedInside) {
        this.reset();
      }
    } else {
      this.reset();
    }
  }
  constructor(
    private _router: Router,
    private store: Store,
    public helper: HelperService,
    public resize: ResizeService,
    private activatedRoute: ActivatedRoute,
    private scrollService: ScrollService,
  ) {
    this.store.dispatch(setSearchState({ search: false }));
    this.categories$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((categories) => {
        this.Categories = categories;
      });
    this.categoryName$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((name) => {
        this.categoryName = name;
      });
    this.searching$
      .pipe(takeUntil(this.destroy$))
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((searching) => {
        this.searching = searching;
      });
    this.search$
      .pipe(takeUntil(this.destroy$))
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((search) => {
        this.searchState = search;
      });
    this.sortCollection$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((value: number) => {
        if (value) {
          this.sortValue = value;
        }
      });
    this.scrollService.scrollData$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((scroll) => {
        this.scrollData = scroll;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkHeaderState(): void {
    // Check if the URL matches "/recordings/<userid>" or "/mega-sessions/<sessionId>"
    const recordingsPattern = /^\/([^\/]+\/recording\/[^\/]+|recordings\/[^\/]+|mega-sessions\/[^\/]+|[^\/]+\/mega-session\/[^\/]+|checkout)$/;

    if (recordingsPattern.test(this.currentUrl)) {
      this.showHeader = false;
    } else {
      this.showHeader = true;
    }
  }

  ngOnInit() {
    this.currentUrl = this._router.url;
    this.checkHeaderState();
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe(() => {
        this.currentUrl = this._router.url;
        this.checkHeaderState()
        this.handleRouteChange();
        this.updateHeaderState(this.activatedRoute.root);
      });
    this.handleRouteChange();
    this.mentorSearchQueryUpdate
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((value) => this.handleMentorSearchQuery(value));
    this.updateHeaderState(this.activatedRoute.root);
  }
  reset() {
    this.show = false;
    this.mentorSearchQuery = null;
    this.searchValue = null;
    this.resetSearch(false);
  }
  private handleMentorSearchQuery(value: string): void {
    if (value != null) {
      if (value.trim() === '') {
        this.resetSearch(true);
      } else {
        this.updateSearch(value);
      }
    } else {
      this.store.dispatch(setSearchState({ search: false }));
    }
  }
  scrollToSelected() {
    setTimeout(() => {
      if (window.innerWidth > 768) {
        window.scrollTo(0, window.innerHeight / 3);
      } else {
        window.scrollTo(0, window.innerHeight / 2.5);
      }
    }, 0);
  }
  resetSearch(redirect: boolean): void {
    this.show = false;
    this.mentorSearchQuery = null;
    if (this.categoryName) {
      const selectedCategory = this.Categories.find(
        (item) => item?.name == this.categoryName,
      );
      this.store.dispatch(setCategoryName({ name: selectedCategory?.name }));
      if (redirect) {
        this._router.navigate(['/creators/' + selectedCategory?.path]);
      }
    } else {
      this.store.dispatch(
        setCategoryName({ name: this.Categories?.[0]?.name }),
      );
      const selectedCategory = this.Categories.find(
        (item) => item?.name == this.categoryName,
      );
      this.store.dispatch(setCategoryName({ name: selectedCategory?.name }));
      if (redirect) {
        this._router.navigate(['/creators/' + selectedCategory?.path]);
      }
    }
    this.store.dispatch(setSearchState({ search: false }));
  }

  private updateSearch(value: string): void {
    this.store.dispatch(setSearchState({ search: true }));
    this.pageNumber = 1;
    this.searchValue = value;
    this.show = true;
  }

  private async initializeCategoryData(): Promise<void> {
    if (this.helper.checkStringAgainstPaths(this.Categories, this.routeName)) {
      this.mentorSearchQuery = this.routeName?.replace(/-/g, ' ');
    }
    this.category =
      this.Categories?.find((cat: any) => cat?.path === this.routeName) ?? null;
    this.subCategory =
      this.category?.subCategory?.find(
        (subCat: any) => subCat?.path === this.subRouteName,
      ) ?? null;
  }

  private async handleRouteChange(): Promise<void> {
    this.routeName = this._router.url.split('/')?.[2];
    this.subRouteName = this._router.url.split('/')?.[3];
    if (this.routeName !== this.mentorSearchQuery) {
      this.mentorSearchQuery = null;
      this.mentorSearchQueryUpdate.next(this.mentorSearchQuery);
      this.store.dispatch(setSearchState({ search: false }));
    }
    await this.initializeCategoryData();
    if (this.scrollPosition == 'center') {
      this.scrollToSelected();
    }
  }

  sortMentor(value: number): void {
    this.store.dispatch(sortCollecton({ value }));
  }
  private updateHeaderState(route: any): void {
    while (route.firstChild) {
      route = route.firstChild;
    }
    const data = route.snapshot.data;
    if (data && Object.keys(data).length !== 0) {
      this.showTray = data.categoryTray || false;
      this.scrollPosition = data?.scroll || 'top';
    }
  }
}
